(() => {
  if (window.MybeCardQuickQtyV13) return;
  window.MybeCardQuickQtyV13 = true;

  const rootUrl = window.Shopify?.routes?.root || '/';
  const cartUrl = path => `${rootUrl}${path}`;
  let cartState = null;
  let cartReady = false;
  let initialLoad = null;
  let workerRunning = false;
  let queueTimer = null;
  let latestChange = null;
  const pending = new Map();
  const inFlight = new Map();
  const DEBOUNCE_MS = 22;
  let lastPreviewSerial = 0;

  const getCart = async () => {
    const response = await fetch(cartUrl('cart.js'), {
      headers:{Accept:'application/json'},
      cache:'no-store'
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to load cart');
    return cart;
  };

  const findLine = (cart, variantId) =>
    (cart?.items || []).find(item => String(item.variant_id) === String(variantId));
  const quantityFor = (cart, variantId) => Number(findLine(cart, variantId)?.quantity || 0);
  const stockLimitFor = control => {
    if (!control || control.dataset.stockManaged !== 'true' ||
        control.dataset.inventoryPolicy === 'continue') return Infinity;
    const available = Number(control.dataset.available);
    return Number.isFinite(available) ? Math.max(0, available) : Infinity;
  };
  const isPaymentFee = item => {
    const title = String(item.product_title || item.title || '').toLowerCase();
    const handle = String(item.handle || item.product_handle || '').toLowerCase();
    return title.includes('payment fee') || handle.includes('payment-fee');
  };
  const visibleItems = cart => (cart?.items || []).filter(item => !isPaymentFee(item));
  const visibleCount = cart => visibleItems(cart).reduce((sum,item) => sum + Number(item.quantity || 0),0);
  const visibleTotal = cart => {
    const feeTotal = (cart?.items || []).filter(isPaymentFee).reduce((sum,item) =>
      sum + Number(item.final_line_price ?? item.line_price ?? 0),0);
    return Math.max(0,Number(cart?.total_price || 0) - feeTotal);
  };

  const getSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
      bubbles:true,
      detail:{sections}
    }));
    document.querySelectorAll('cart-drawer[data-section-id], main-cart[data-section-id]').forEach(element => {
      const id = element.dataset.sectionId;
      if (id) sections.push(id);
    });
    return [...new Set(sections)].slice(0,5);
  };

  const matchingControls = variantId =>
    [...document.querySelectorAll('[data-mybe-card-qty]')]
      .filter(control => String(control.dataset.variantId) === String(variantId));

  const setControl = (control, quantity, direction = 0) => {
    const inCart = quantity > 0;
    control._mybeTarget = quantity;
    control._mybeDirty = pending.has(String(control.dataset.variantId));
    control.dataset.qtyState = !inCart ? 'empty' : quantity === 1 ? 'one' : 'multiple';

    const count = control.querySelector('[data-qty-count]');
    const minus = control.querySelector('[data-qty-minus]');
    const addButton = control.querySelector('[data-qty-add]');
    const plus = control.querySelector('[data-qty-plus]');
    const stepper = control.querySelector('[data-qty-stepper]');
    const stockLimit = stockLimitFor(control);
    const atStockLimit = Number.isFinite(stockLimit) && quantity >= stockLimit;

    if (count) count.textContent = String(quantity);
    if (plus) {
      plus.disabled = atStockLimit;
      plus.setAttribute('aria-disabled', atStockLimit ? 'true' : 'false');
      plus.setAttribute('aria-label', atStockLimit ? 'Maximum available quantity reached' : 'Add one');
      plus.classList.toggle('is-stock-limit', atStockLimit);
    }
    if (minus) {
      minus.disabled = !inCart;
      minus.setAttribute('aria-label', quantity <= 1 ? 'Remove item' : 'Decrease quantity');
      minus.dataset.qtyState = quantity <= 1 ? 'one' : 'multiple';
    }
    if (addButton) addButton.hidden = true;
    if (stepper) stepper.hidden = false;
    control.classList.toggle('is-in-cart', inCart);
    control.classList.toggle('is-empty', !inCart);
    if (direction) flash(control, direction);
  };

  const syncControls = () => {
    document.querySelectorAll('[data-mybe-card-qty]').forEach(control => {
      const id = String(control.dataset.variantId);
      const desired = pending.get(id)?.target ?? quantityFor(cartState,id);
      setControl(control,desired);
    });
  };

  const updateQuickCartOptimistically = (forcedDirection = '') => {
    if (!cartState) return;
    let countDelta = 0;
    let totalDelta = 0;

    pending.forEach((entry, variantId) => {
      const confirmed = quantityFor(cartState,variantId);
      const delta = entry.target - confirmed;
      const line = findLine(cartState,variantId);
      const control = matchingControls(variantId)[0];
      const unitPrice = line
        ? Number(line.final_line_price || line.line_price || 0) / Math.max(1,Number(line.quantity || 1))
        : Number(control?.dataset.unitPrice || 0);
      countDelta += delta;
      totalDelta += delta * unitPrice;
    });

    const item_count = Math.max(0,visibleCount(cartState)+countDelta);
    const total_price = Math.max(0,visibleTotal(cartState)+totalDelta);
    const quantities = {};
    pending.forEach((entry, id) => { quantities[id] = entry.target; });
    // One preview drives the floating cart, header counters, drawer rows and totals.
    // DOM is not replaced until the last network write is reconciled.
    document.dispatchEvent(new CustomEvent('mybe:cart-preview', {
      detail: {cart:cartState, quantities, item_count, total_price,
        currency:cartState.currency || 'EGP', serial:++lastPreviewSerial}
    }));
    document.dispatchEvent(new CustomEvent('mybe:cart-optimistic',{
      detail:{item_count,total_price,currency:cartState.currency || 'EGP',direction:forcedDirection}
    }));
  };

  function flash(control,direction) {
    control.classList.remove('is-added','is-removed');
    control.classList.add(direction > 0 ? 'is-added' : 'is-removed');
    const count = control.querySelector('[data-qty-count]');
    if (count) {
      count.classList.remove('is-changing');
      count.classList.add('is-changing');
    }
    clearTimeout(control._mybeFlashTimer);
    control._mybeFlashTimer = setTimeout(() => {
      control.classList.remove('is-added','is-removed');
      count?.classList.remove('is-changing');
    },420);
  }

  const requestChange = async (variantId,target) => {
    const execute = async () => {
      const sharedCart = window.MybeCartWriteQueue?.lastCart;
      if (sharedCart && Array.isArray(sharedCart.items)) cartState = sharedCart;

      const current = quantityFor(cartState,variantId);
      const line = findLine(cartState,variantId);
      const sections = getSections();
      let response;

      if (current === 0 && target > 0) {
        response = await fetch(cartUrl('cart/add.js'),{
          method:'POST',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({
            id:Number(variantId), quantity:target,
            sections, sections_url:window.location.pathname
          })
        });
      } else {
        if (!line?.key) throw new Error(`Cart line not found for variant ${variantId}`);
        response = await fetch(cartUrl('cart/change.js'),{
          method:'POST',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({
            id:line.key, quantity:target,
            sections, sections_url:window.location.pathname
          })
        });
      }

      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Shopify rejected the cart update');
      }
      let updated = result;
      if (typeof result.item_count !== 'number' || !Array.isArray(result.items)) {
        // cart/add returns only the added line; read the full cart before releasing the lock.
        updated = await getCart();
        if (result.sections) updated.sections = result.sections;
      }
      if (window.MybeCartWriteQueue) window.MybeCartWriteQueue.lastCart = updated;
      return updated;
    };

    return window.MybeCartWriteQueue
      ? await window.MybeCartWriteQueue.run(execute)
      : await execute();
  };

  const publishFinalCart = (cart,direction = '') => {
    if (!cart) return;
    const sections = cart.sections || {};
    const requiredSectionIds = [...new Set([
      ...[...document.querySelectorAll('cart-drawer[data-section-id]')].map(el => el.dataset.sectionId),
      ...[...document.querySelectorAll('main-cart[data-section-id]')].map(el => el.dataset.sectionId)
    ].filter(Boolean))];
    const hasRequiredSections = requiredSectionIds.length > 0 &&
      requiredSectionIds.every(id => typeof sections[id] === 'string');

    if (hasRequiredSections && window.theme?.pubsub?.publish &&
        window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate,{
        source:'mybe-card-quick-qty',
        cart,
        line:latestChange?.line,
        name:latestChange?.name
      });
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh',{detail:{open:false}}));
    }

    // Reconcile the floating cart with Shopify's confirmed state, including stock clamps.
    document.dispatchEvent(new CustomEvent('mybe:cart-confirmed',{detail:{cart,direction}}));
    document.dispatchEvent(new CustomEvent('cart:updated',{
      detail:{cart,source:'mybe-card-quick-qty'}
    }));
  };

  const scheduleQueue = () => {
    clearTimeout(queueTimer);
    queueTimer = setTimeout(runQueue,DEBOUNCE_MS);
  };

  const runQueue = async () => {
    queueTimer = null;
    if (workerRunning || !cartReady) return;
    workerRunning = true;
    const batchStartCount = visibleCount(cartState);
    let changed = false;

    try {
      while (pending.size) {
        const [variantId,entry] = pending.entries().next().value;
        const current = quantityFor(cartState,variantId);
        const target = Math.max(0,Number(entry.target || 0));

        if (target === current) {
          pending.delete(variantId);
          syncControls();
          updateQuickCartOptimistically();
          continue;
        }

        const matching = matchingControls(variantId);
        const max = stockLimitFor(matching[0]);
        if (target > current && Number.isFinite(max) && target > max) {
          pending.delete(variantId);
          syncControls();
          updateQuickCartOptimistically();
          continue;
        }

        matching.forEach(control => control.classList.add('is-loading'));
        inFlight.set(variantId,{target,version:entry.version});

        try {
          const updated = await requestChange(variantId,target);
          cartState = updated;
          changed = true;

          const actual = quantityFor(cartState,variantId);
          const latest = pending.get(variantId);

          // If clicks arrived during the request, preserve the newest desired quantity,
          // even when the user clicked back to the pre-request quantity.
          if (latest && latest.version === entry.version && latest.target === target) {
            // Shopify may clamp a quantity because of stock/rules; accept the actual value.
            pending.delete(variantId);
          } else if (latest && actual === latest.target) {
            pending.delete(variantId);
          } else if (latest) {
            latest.base = actual;
          }

          // External product forms or drawer updates cannot leave other cards stale.
          syncControls();
          updateQuickCartOptimistically();
        } catch(error) {
          console.error('[Mybe quick quantity]',error);
          const fresh = await getCart().catch(() => null);
          if (fresh) cartState = fresh;
          pending.delete(variantId);
          syncControls();
          updateQuickCartOptimistically();
          document.dispatchEvent(new CustomEvent('mybe:quick-add-error',{
            detail:{error,variantId}
          }));
        } finally {
          inFlight.delete(variantId);
          matching.forEach(control => control.classList.remove('is-loading'));
        }
      }
    } finally {
      workerRunning = false;
      syncControls();
      updateQuickCartOptimistically();
      if (changed && !window.MybeCartClearing) {
        const finalCount = visibleCount(cartState);
        const direction = finalCount > batchStartCount ? 'added' :
          finalCount < batchStartCount ? 'removed' : '';
        publishFinalCart(cartState,direction);
      } else if (!pending.size && !inFlight.size && cartState && !window.MybeCartClearing) {
        // Even a + / - burst that cancels itself must release the Checkout
        // updating state; no Shopify write was necessary in this case.
        document.dispatchEvent(new CustomEvent('mybe:cart-confirmed', {
          detail: {cart:cartState,direction:''}
        }));
      }
      if (pending.size) scheduleQueue();
    }
  };

  // A line can be safely controlled by variant ID only when there is exactly
  // one matching Shopify line, without properties or selling plans. Other lines
  // retain their own line-key-based native drawer updater.
  const canHandleDrawer = (lineKey, variantId) => {
    if (!cartReady || !cartState || !variantId || window.MybeCartClearing) return false;
    const matching = (cartState.items || []).filter(item => String(item.variant_id) === String(variantId));
    if (matching.length !== 1) return false;
    // Shopify can rotate the line key mid-write. The drawer keeps its existing
    // DOM during the burst, so trust the unique variant while its write is active.
    if (String(matching[0].key) !== String(lineKey) &&
        !pending.has(String(variantId)) && !inFlight.has(String(variantId))) return false;
    const line = matching[0];
    return !Object.keys(line.properties || {}).length && !line.selling_plan && !line.selling_plan_allocation;
  };

  const setDesiredQuantity = (variantId, desired, options = {}) => {
    if (!cartReady || !cartState || window.MybeCartClearing) return false;
    variantId = String(variantId);
    const confirmed = quantityFor(cartState,variantId);
    const existing = pending.get(variantId);
    const previous = existing ? existing.target : confirmed;
    const target = Math.max(0,Math.floor(Number(desired) || 0));
    const controls = matchingControls(variantId);
    const sourceLimit = Number(options.stockLimit);
    const max = Number.isFinite(sourceLimit) && options.stockLimit !== undefined
      ? sourceLimit : stockLimitFor(controls[0]);
    if (target > previous && Number.isFinite(max) && target > max) {
      document.dispatchEvent(new CustomEvent('mybe:cart-limit'));
      return false;
    }
    if (target === previous) return true;
    const direction = target > previous ? 1 : -1;
    latestChange = {
      line:findLine(cartState,variantId)?.key || null,
      name:'updates[]'
    };
    const version = (existing?.version || 0) + 1;
    if (target === confirmed && !inFlight.has(variantId)) pending.delete(variantId);
    else pending.set(variantId, {target,base:existing?.base ?? confirmed,version});
    controls.forEach(control => setControl(control,target,direction));
    updateQuickCartOptimistically(direction > 0 ? 'added' : 'removed');
    scheduleQueue();
    return true;
  };

  document.addEventListener('click',async event => {
    const add = event.target.closest('[data-qty-add]');
    const plus = event.target.closest('[data-qty-plus]');
    const minus = event.target.closest('[data-qty-minus]');
    if (!add && !plus && !minus) return;
    if (window.MybeCartClearing) return;
    const control = event.target.closest('[data-mybe-card-qty]');
    if (!control) return;
    event.preventDefault();
    event.stopPropagation();
    if (!cartReady) {
      try { await initializeCart(); }
      catch (error) {
        document.dispatchEvent(new CustomEvent('mybe:quick-add-error', {detail:{error}}));
        return;
      }
      if (!control.isConnected || window.MybeCartClearing) return;
    }
    const variantId = String(control.dataset.variantId);
    const current = pending.get(variantId)?.target ?? quantityFor(cartState,variantId);
    const direction = minus ? -1 : 1;
    const target = Math.max(0,current + direction);
    if (direction < 0 && current === 0) return;
    setDesiredQuantity(variantId,target,{stockLimit:stockLimitFor(control)});
  });

  const syncFromExternalCart = cart => {
    if (!cart || !Array.isArray(cart.items)) return;
    const previous = cartState;
    cartState = cart;
    if (window.MybeCartWriteQueue) window.MybeCartWriteQueue.lastCart = cart;

    pending.forEach((entry,variantId) => {
      const oldBase = quantityFor(previous,variantId);
      const delta = entry.target - oldBase;
      const newBase = quantityFor(cart,variantId);
      entry.base = newBase;
      entry.target = Math.max(0,newBase + delta);
      if (entry.target === newBase) pending.delete(variantId);
    });

    syncControls();
    updateQuickCartOptimistically();
    if (pending.size) scheduleQueue();
  };

  if (window.theme?.pubsub?.subscribe && window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
    window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate,event => {
      if (event?.cart?.errors || event?.source === 'mybe-card-quick-qty') return;
      syncFromExternalCart(event.cart);
    });
  }

  window.MybeCardQuickQty = {
    refresh:syncFromExternalCart,
    canHandleDrawer,
    setQuantity:setDesiredQuantity,
    getDesiredQuantity:id => pending.get(String(id))?.target ?? quantityFor(cartState,id),
    cancelPending:() => {
      clearTimeout(queueTimer);
      queueTimer = null;
      pending.clear();
      syncControls();
      updateQuickCartOptimistically();
    },
    getCart:() => cartState,
    isBusy:() => workerRunning || pending.size > 0 || inFlight.size > 0 || queueTimer !== null,
    getPending:() => [...pending.entries()].map(([id,value])=>({id,...value}))
  };

  function initializeCart() {
    if (initialLoad) return initialLoad;
    initialLoad = (window.MybeInitialCart?.load ? window.MybeInitialCart.load() : getCart()).then(cart => {
      if (!Array.isArray(cart?.items)) throw new Error('Cart data unavailable');
      cartState = window.MybeCartWriteQueue?.lastCart || cart;
      if (window.MybeCartWriteQueue) window.MybeCartWriteQueue.lastCart = cartState;
      cartReady = true;
      syncControls();
      updateQuickCartOptimistically();
    }).finally(() => { initialLoad = null; });
    return initialLoad;
  }

  window.addEventListener('pageshow', async event => {
    if (!event.persisted || window.MybeCardQuickQty.isBusy() || window.MybeCartClearing) return;
    try {
      const cart = await getCart();
      if (window.MybeCardQuickQty.isBusy() || window.MybeCartClearing) return;
      cartReady = true;
      syncFromExternalCart(cart);
      publishFinalCart(cart);
    } catch (error) {
      console.warn('[Mybe cart restore]', error);
    }
  });

  initializeCart().catch(error => console.warn('[Mybe quick quantity]',error));
})();
